'use client';

import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useWallet } from './use-wallet';

export interface TokenBalance {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  icon: string | null;
  balance: string;
  formatted: string;
}

interface TokenBalancesResponse {
  eth: { balance: string; formatted: string };
  tokens: TokenBalance[];
}

interface TokenBalancesExtendedResponse {
  tokens: TokenBalance[];
}

async function fetchTokenBalances(address: string): Promise<TokenBalancesResponse> {
  const res = await fetch(`/api/rpc/token-balances/${address}`);
  if (!res.ok) throw new Error('Failed to fetch token balances');
  const json = await res.json();
  return json.data;
}

async function fetchTokenBalancesExtended(address: string): Promise<TokenBalancesExtendedResponse> {
  const res = await fetch(`/api/rpc/token-balances-extended/${address}`);
  if (!res.ok) throw new Error('Failed to fetch extended token balances');
  const json = await res.json();
  return json.data;
}

export interface TokenInfo {
  address: string;
  name: string;
  symbol: string;
  decimals: number;
}

export async function resolveTokenInfo(address: string): Promise<TokenInfo | null> {
  try {
    const res = await fetch(`/api/rpc/token-info/${address}`);
    if (!res.ok) return null;
    const json = await res.json();
    return json.data ?? null;
  } catch {
    return null;
  }
}

export function useTokenBalances() {
  const { isConnected, address } = useWallet();

  // Address segment lowercased to dedupe against checksummed/lowercase
  // wallet flips that would otherwise produce two observers + two
  // upstream RPC reads for the same wallet.
  const addressKey = address?.toLowerCase() ?? null;

  const coreQuery = useQuery({
    queryKey: ['token-balances', addressKey],
    queryFn: () => fetchTokenBalances(address!),
    enabled: isConnected && !!address,
    // `staleTime` matched to `refetchInterval` so new observers
    // mounting inside the poll window don't kick extra fetches.
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const extendedQuery = useQuery({
    queryKey: ['token-balances-extended', addressKey],
    queryFn: () => fetchTokenBalancesExtended(address!),
    enabled: isConnected && !!address,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const refetch = useCallback(async () => {
    await Promise.all([coreQuery.refetch(), extendedQuery.refetch()]);
  }, [coreQuery.refetch, extendedQuery.refetch]);

  return {
    ethBalance: coreQuery.data?.eth ?? null,
    tokens: coreQuery.data?.tokens ?? [],
    extendedTokens: extendedQuery.data?.tokens ?? [],
    isLoading: coreQuery.isLoading,
    isLoadingExtended: extendedQuery.isLoading,
    error: coreQuery.error ?? extendedQuery.error,
    refetch,
  };
}
